// WorldChunkManager.js - 大地图区块按需加载
import DebugHelper from './DebugHelper.js';
import MessageHelper from './MessageHelper.js';
import { ensureMapTextures } from './LazyAssetLoader.js';
import {
    findChunkEntry,
    getChunkCoordsInRadius,
    getChunkKeyFromTile,
    globalTileToChunk,
    pixelToGlobalTile
} from './WorldChunkCoords.js';

export default class WorldChunkManager {
    constructor(scene) {
        this.scene = scene;
        this.meta = scene.worldMapJson;
        this.loadRadius = this.meta.loadRadius ?? 1;
        this.unloadRadius = this.loadRadius + 1;
        this.loadedChunkData = new Map();
        this._updateTimer = 0;
        this._updateInterval = 250;
        this._loading = new Set();
        this.currentChunkKey = null;
    }

    /**
     * 初始化并加载玩家周围区块
     */
    async initialize() {
        const player = this.scene.player;
        const pixelX = player?.x ?? this.getSpawnPixelX();
        const pixelY = player?.y ?? this.getSpawnPixelY();

        this.scene.worldMap.createChunkedWorld(this.meta);
        await this.syncChunksAround(pixelX, pixelY, true);
    }

    getSpawnPixelX() {
        const spawn = this.meta.defaultSpawn;
        const tileSize = 32;
        const globalTileX = (spawn?.gridX || 0) * this.meta.chunkWidth + (spawn?.tileX || 0);
        return globalTileX * tileSize + tileSize / 2;
    }

    getSpawnPixelY() {
        const spawn = this.meta.defaultSpawn;
        const tileSize = 32;
        const globalTileY = (spawn?.gridY || 0) * this.meta.chunkHeight + (spawn?.tileY || 0);
        return globalTileY * tileSize + tileSize / 2;
    }

    /**
     * @param {number} time
     * @param {Phaser.GameObjects.Sprite|null} sprite
     */
    update(time, sprite) {
        if (!sprite || this._loading.size > 0) {
            return;
        }

        if (time - this._updateTimer < this._updateInterval) {
            return;
        }

        this._updateTimer = time;
        this.checkRegionChange(sprite);
        this.syncChunksAround(sprite.x, sprite.y, false).catch((error) => {
            DebugHelper.warn('WorldChunkManager: 区块同步失败', error);
        });
    }

    /**
     * 传送后强制刷新周围区块
     */
    async refreshAroundSprite(sprite) {
        if (!sprite) return;
        await this.syncChunksAround(sprite.x, sprite.y, true);
        this.notifyCurrentRegion();
    }

    /**
     * 进入大地图或传送后，显示当前区块名称
     */
    notifyCurrentRegion() {
        const sprite = this.scene.playerSprite;
        if (!sprite) return;

        const chunkKey = this.getCurrentChunkKey(sprite);
        this.currentChunkKey = chunkKey;

        const name = this.getRegionName(chunkKey);
        if (name) {
            MessageHelper.showSceneNotification(this.scene, name, 1000);
        }
    }

    /**
     * 玩家移动跨越区块边界时提示区域名
     */
    checkRegionChange(sprite) {
        if (!sprite || this.scene.worldUI?.isDialogActive) {
            return;
        }

        const chunkKey = this.getCurrentChunkKey(sprite);
        if (!chunkKey || chunkKey === this.currentChunkKey) {
            return;
        }

        this.currentChunkKey = chunkKey;
        const name = this.getRegionName(chunkKey);
        if (name) {
            MessageHelper.showSceneNotification(this.scene, name, 1000);
        }
    }

    getCurrentChunkKey(sprite) {
        const { globalTileX, globalTileY } = pixelToGlobalTile(this.meta, sprite.x, sprite.y);
        return getChunkKeyFromTile(this.meta, globalTileX, globalTileY);
    }

    getRegionName(chunkKey) {
        const [gridX, gridY] = chunkKey.split('_').map(Number);
        const entry = findChunkEntry(this.meta, gridX, gridY);
        if (entry?.name) {
            return entry.name;
        }

        const chunkJson = this.loadedChunkData.get(chunkKey);
        return chunkJson?.name || null;
    }

    /**
     * @param {number} pixelX
     * @param {number} pixelY
     * @param {boolean} force
     */
    async syncChunksAround(pixelX, pixelY, force = false) {
        const required = getChunkCoordsInRadius(this.meta, pixelX, pixelY, this.loadRadius);
        const requiredKeys = new Set(required.map(({ gridX, gridY }) => `${gridX}_${gridY}`));

        for (const { gridX, gridY } of required) {
            const chunkKey = `${gridX}_${gridY}`;
            if (!this.scene.worldMap.isChunkLoaded(chunkKey) || force) {
                await this.loadChunk(gridX, gridY);
            }
        }

        const unloadKeys = [];
        this.scene.worldMap.loadedChunks.forEach((_value, chunkKey) => {
            const [gridX, gridY] = chunkKey.split('_').map(Number);
            const { globalTileX, globalTileY } = pixelToGlobalTile(this.meta, pixelX, pixelY);
            const center = globalTileToChunk(this.meta, globalTileX, globalTileY);
            const distance = Math.max(Math.abs(center.gridX - gridX), Math.abs(center.gridY - gridY));

            if (distance > this.unloadRadius) {
                unloadKeys.push(chunkKey);
            }
        });

        unloadKeys.forEach((chunkKey) => {
            if (!requiredKeys.has(chunkKey)) {
                this.unloadChunk(chunkKey);
            }
        });
    }

    /**
     * @param {number} gridX
     * @param {number} gridY
     */
    async loadChunk(gridX, gridY) {
        const chunkKey = `${gridX}_${gridY}`;
        if (this._loading.has(chunkKey) || this.scene.worldMap.isChunkLoaded(chunkKey)) {
            return;
        }

        const entry = findChunkEntry(this.meta, gridX, gridY);
        if (!entry) {
            DebugHelper.warn(`WorldChunkManager: 未找到区块配置 (${gridX}, ${gridY})`);
            return;
        }

        this._loading.add(chunkKey);

        try {
            const chunkJson = await this.fetchChunkJson(entry.dataKey, entry.dataPath);
            await ensureMapTextures(this.scene, chunkJson);
            this.scene.worldMap.loadChunk(gridX, gridY, chunkJson, chunkKey);
            this.loadedChunkData.set(chunkKey, chunkJson);
            DebugHelper.log(`WorldChunkManager: 已加载区块 ${chunkKey} (${entry.name})`);
        } finally {
            this._loading.delete(chunkKey);
        }
    }

    /**
     * @param {string} chunkKey
     */
    unloadChunk(chunkKey) {
        if (!this.scene.worldMap.isChunkLoaded(chunkKey)) {
            return;
        }

        this.scene.worldMap.unloadChunk(chunkKey);
        this.loadedChunkData.delete(chunkKey);
        DebugHelper.log(`WorldChunkManager: 已卸载区块 ${chunkKey}`);
    }

    /**
     * @param {string} dataKey
     * @param {string} dataPath
     */
    fetchChunkJson(dataKey, dataPath) {
        if (this.scene.cache.json.exists(dataKey)) {
            return Promise.resolve(this.scene.cache.json.get(dataKey));
        }

        return new Promise((resolve, reject) => {
            this.scene.load.json(dataKey, dataPath);

            const onComplete = () => {
                this.scene.load.off('loaderror', onError);
                resolve(this.scene.cache.json.get(dataKey));
            };
            const onError = (file) => {
                this.scene.load.off('complete', onComplete);
                reject(new Error(`区块 JSON 加载失败: ${file?.key || dataKey}`));
            };

            this.scene.load.once('complete', onComplete);
            this.scene.load.once('loaderror', onError);

            if (!this.scene.load.isLoading()) {
                this.scene.load.start();
            }
        });
    }

    /**
     * 获取玩家当前所在区块的遇敌配置
     * @param {Phaser.GameObjects.Sprite} sprite
     */
    getEncounterConfigAtSprite(sprite) {
        if (!sprite) {
            return this.meta.sceneConfig || {};
        }

        const { globalTileX, globalTileY } = pixelToGlobalTile(this.meta, sprite.x, sprite.y);
        const chunkKey = getChunkKeyFromTile(this.meta, globalTileX, globalTileY);
        const chunkJson = this.loadedChunkData.get(chunkKey);

        if (chunkJson?.sceneConfig) {
            return chunkJson.sceneConfig;
        }

        return this.meta.sceneConfig || {};
    }

    /**
     * 获取玩家当前区块的 randomEnemies
     * @param {Phaser.GameObjects.Sprite} sprite
     */
    getRandomEnemiesAtSprite(sprite) {
        const { globalTileX, globalTileY } = pixelToGlobalTile(this.meta, sprite.x, sprite.y);
        const chunkKey = getChunkKeyFromTile(this.meta, globalTileX, globalTileY);
        const chunkJson = this.loadedChunkData.get(chunkKey);
        return chunkJson?.randomEnemies || [];
    }

    destroy() {
        this.scene.worldMap?.unloadAllChunks?.();
        this.loadedChunkData.clear();
        this._loading.clear();
    }
}
